import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSpecialChar: boolean;
}

type PasswordValidationError =
  | 'too_short'
  | 'too_long'
  | 'missing_uppercase'
  | 'missing_lowercase'
  | 'missing_digit'
  | 'missing_special_char';

function validatePassword(password: string, policy: PasswordPolicy): { valid: boolean; errors: PasswordValidationError[] } {
  const errors: PasswordValidationError[] = [];

  if (password.length < policy.minLength) errors.push('too_short');
  if (password.length > policy.maxLength) errors.push('too_long');
  if (policy.requireUppercase && !/[A-Z]/.test(password)) errors.push('missing_uppercase');
  if (policy.requireLowercase && !/[a-z]/.test(password)) errors.push('missing_lowercase');
  if (policy.requireDigit && !/\d/.test(password)) errors.push('missing_digit');
  if (policy.requireSpecialChar && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    errors.push('missing_special_char');
  }

  return { valid: errors.length === 0, errors };
}

interface PasswordRequirement {
  key: PasswordValidationError;
  labelKey: string;
  met: boolean;
}

/**
 * Real-time password strength indicator component.
 *
 * Displays a progress bar and a checklist of requirements based on the
 * active tenant password policy.
 */
@Component({
  selector: 'zag-password-strength',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="zag-password-strength" aria-live="polite">
      @if (password()) {
        <div class="zag-password-strength__bar-track" [attr.aria-label]="'passwordStrength.' + strengthClass() | translate">
          <div
            class="zag-password-strength__bar-fill"
            [class.zag-password-strength__bar-fill--weak]="strengthClass() === 'weak'"
            [class.zag-password-strength__bar-fill--fair]="strengthClass() === 'fair'"
            [class.zag-password-strength__bar-fill--good]="strengthClass() === 'good'"
            [class.zag-password-strength__bar-fill--strong]="strengthClass() === 'strong'"
            [style.width.%]="strengthPercent()"
            role="progressbar"
            [attr.aria-valuenow]="strengthPercent()"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        <ul class="zag-password-strength__list" role="list">
          @for (req of requirements(); track req.key) {
            <li
              class="zag-password-strength__req"
              [class.zag-password-strength__req--met]="req.met"
              [class.zag-password-strength__req--unmet]="!req.met"
            >
              <span class="zag-password-strength__req-icon" aria-hidden="true">{{ req.met ? '✓' : '○' }}</span>
              {{ req.labelKey | translate: reqParams(req) }}
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: `
    .zag-password-strength {
      margin-top: var(--spacing-xs);
    }

    .zag-password-strength__bar-track {
      height: var(--spacing-xs);
      background-color: var(--surface-color-secondary);
      border-radius: var(--border-radius-sm);
      overflow: hidden;
      margin-bottom: var(--spacing-xs);
    }

    .zag-password-strength__bar-fill {
      height: 100%;
      border-radius: var(--border-radius-sm);
      transition:
        width var(--transition-duration-fast) ease,
        background-color var(--transition-duration-fast) ease;
    }

    .zag-password-strength__bar-fill--weak {
      background-color: var(--color-error);
    }

    .zag-password-strength__bar-fill--fair {
      background-color: var(--color-warning);
    }

    .zag-password-strength__bar-fill--good {
      background-color: var(--color-info);
    }

    .zag-password-strength__bar-fill--strong {
      background-color: var(--color-success);
    }

    .zag-password-strength__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .zag-password-strength__req {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: 12px;
      transition: color var(--transition-duration-fast) ease;
    }

    .zag-password-strength__req--met {
      color: var(--color-success);
    }

    .zag-password-strength__req--unmet {
      color: var(--text-color-secondary);
    }

    .zag-password-strength__req-icon {
      font-size: 10px;
      width: 12px;
      text-align: center;
    }
  `,
})
export class PasswordStrengthComponent {
  readonly password = input.required<string>();
  readonly policy = input.required<PasswordPolicy>();

  protected readonly requirements = computed((): PasswordRequirement[] => {
    const pwd = this.password();
    const pol = this.policy();
    const result = validatePassword(pwd, pol);
    const errors = new Set(result.errors);

    const reqs: PasswordRequirement[] = [{ key: 'too_short', labelKey: 'passwordStrength.tooShort', met: !errors.has('too_short') }];

    if (pol.maxLength < 256) {
      reqs.push({ key: 'too_long', labelKey: 'passwordStrength.tooLong', met: !errors.has('too_long') });
    }
    if (pol.requireUppercase) {
      reqs.push({ key: 'missing_uppercase', labelKey: 'passwordStrength.missingUppercase', met: !errors.has('missing_uppercase') });
    }
    if (pol.requireLowercase) {
      reqs.push({ key: 'missing_lowercase', labelKey: 'passwordStrength.missingLowercase', met: !errors.has('missing_lowercase') });
    }
    if (pol.requireDigit) {
      reqs.push({ key: 'missing_digit', labelKey: 'passwordStrength.missingDigit', met: !errors.has('missing_digit') });
    }
    if (pol.requireSpecialChar) {
      reqs.push({ key: 'missing_special_char', labelKey: 'passwordStrength.missingSpecialChar', met: !errors.has('missing_special_char') });
    }

    return reqs;
  });

  protected readonly strengthPercent = computed(() => {
    const reqs = this.requirements();
    if (!reqs.length) return 0;
    return Math.round((reqs.filter(r => r.met).length / reqs.length) * 100);
  });

  protected readonly strengthClass = computed((): 'weak' | 'fair' | 'good' | 'strong' => {
    const pct = this.strengthPercent();
    if (pct < 25) return 'weak';
    if (pct < 50) return 'fair';
    if (pct < 75) return 'good';
    return 'strong';
  });

  protected reqParams(req: PasswordRequirement): Record<string, unknown> {
    const pol = this.policy();
    if (req.key === 'too_short') return { min: pol.minLength };
    if (req.key === 'too_long') return { max: pol.maxLength };
    return {};
  }
}
